// Probabilidade de Sucesso (F3 v0) — pilar hero nº 2.
// Anel + faixa de confiança + drivers explicáveis + cenários "e se".

import type { Goal } from '@app-types/domain';
import { computeProbability } from '@core/probability';
import { useStore } from '@ui/lib/useStore';
import { useState } from 'preact/hooks';

function pointColor(p: number): string {
  if (p >= 70) return 'var(--green)';
  if (p >= 45) return 'var(--amber)';
  return 'var(--red)';
}

const IMPACT_ICON = { up: '▲', down: '▼', flat: '◆' } as const;
const IMPACT_COLOR = {
  up: 'var(--green)',
  down: 'var(--red)',
  flat: 'var(--text-dim)',
} as const;

export function ProbabilityCard(props: { goal: Goal }) {
  const sessions = useStore((s) => s.sessions);
  const [scenarioId, setScenarioId] = useState<'keep' | 'plus1h' | 'minus20'>('keep');

  const result = computeProbability(props.goal, sessions);

  if (!result.ready) {
    return (
      <div class="panel" style={{ marginBottom: '20px' }}>
        <div class="panel-title">
          ◎ <b>PROBABILIDADE DE SUCESSO</b>
          <span class="tag purple" style={{ fontSize: '9px' }}>
            NOVO · v0
          </span>
        </div>
        <div
          style={{
            fontFamily: 'var(--f-mono)',
            fontSize: '12px',
            color: 'var(--text-dim)',
            lineHeight: 1.7,
          }}
        >
          {result.reason}
        </div>
      </div>
    );
  }

  const scenario = result.scenarios.find((s) => s.id === scenarioId) ?? result.scenarios[0];
  const shown = scenarioId === 'keep' ? result.point : scenario.point;
  const color = pointColor(shown);
  const R = 52;
  const CIRC = 2 * Math.PI * R;

  return (
    <div class="panel" style={{ marginBottom: '20px' }}>
      <div class="panel-title">
        ◎ <b>PROBABILIDADE DE SUCESSO</b> · {props.goal.title}
        <span class="tag purple" style={{ fontSize: '9px' }}>
          NOVO · v0
        </span>
      </div>

      <div class="split wide-right" style={{ alignItems: 'center' }}>
        {/* ANEL */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ position: 'relative', width: '170px', height: '170px', margin: '0 auto' }}>
            <svg
              viewBox="0 0 130 130"
              style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}
            >
              <circle
                cx="65"
                cy="65"
                r={R}
                fill="none"
                stroke="rgba(0,240,255,0.08)"
                stroke-width="10"
              />
              <circle
                cx="65"
                cy="65"
                r={R}
                fill="none"
                stroke={color}
                stroke-width="10"
                stroke-linecap="round"
                stroke-dasharray={CIRC}
                stroke-dashoffset={CIRC * (1 - shown / 100)}
                style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s ease' }}
              />
            </svg>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--f-heading)',
                  fontSize: '38px',
                  fontWeight: 900,
                  color,
                  lineHeight: 1,
                }}
              >
                {shown}%
              </div>
              {scenarioId === 'keep' && (
                <div
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: '10px',
                    color: 'var(--text-dim)',
                    marginTop: '6px',
                  }}
                >
                  faixa {result.range[0]}–{result.range[1]}%
                </div>
              )}
            </div>
          </div>

          {/* cenários */}
          <div
            style={{
              display: 'flex',
              gap: '6px',
              justifyContent: 'center',
              marginTop: '12px',
              flexWrap: 'wrap',
            }}
          >
            {result.scenarios.map((s) => (
              <button
                key={s.id}
                type="button"
                class={`btn sm ${scenarioId === s.id ? 'primary' : 'ghost'}`}
                onClick={() => setScenarioId(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div
            style={{
              fontFamily: 'var(--f-mono)',
              fontSize: '10px',
              color: 'var(--text-dim)',
              marginTop: '8px',
            }}
          >
            {scenario.h_per_week}h/sem
            {scenario.finish_weeks !== null && scenario.finish_weeks > 0
              ? ` · cobre as horas restantes em ~${scenario.finish_weeks} sem`
              : ''}
          </div>
        </div>

        {/* DRIVERS + NÚMEROS */}
        <div>
          <div class="grid-3" style={{ marginBottom: '14px' }}>
            <div class="kpi">
              <div class="label">Feitas</div>
              <div class="value" style={{ fontSize: '20px' }}>
                {result.hours_done}h
              </div>
              <div class="sub">de {result.hours_target}h</div>
            </div>
            <div class="kpi purple">
              <div class="label">Ritmo atual</div>
              <div class="value" style={{ fontSize: '20px' }}>
                {result.current_h_per_week}h
              </div>
              <div class="sub">por semana</div>
            </div>
            <div class="kpi pink">
              <div class="label">Necessário</div>
              <div class="value" style={{ fontSize: '20px' }}>
                {result.needed_h_per_week}h
              </div>
              <div class="sub">por semana</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {result.drivers.map((d) => (
              <div
                key={d.label}
                style={{
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'baseline',
                  padding: '8px 12px',
                  background: 'rgba(0,8,24,0.4)',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 'var(--r-sm)',
                }}
              >
                <span
                  style={{
                    color: IMPACT_COLOR[d.impact],
                    fontFamily: 'var(--f-mono)',
                    fontSize: '10px',
                  }}
                >
                  {IMPACT_ICON[d.impact]}
                </span>
                <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 600 }}>
                  {d.label}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontFamily: 'var(--f-mono)',
                    fontSize: '11px',
                    color: 'var(--text-dim)',
                    textAlign: 'right',
                  }}
                >
                  {d.detail}
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '12px',
              fontFamily: 'var(--f-mono)',
              fontSize: '10px',
              color: 'var(--text-mute)',
              lineHeight: 1.6,
            }}
          >
            Modelo determinístico v0 (ritmo vs. necessário, ajustado por consistência).
            {result.baseline_name ? ` Horas-alvo pelo baseline ${result.baseline_name}.` : ''} Fica
            bayesiano quando a base tiver aprovados reais.
          </div>
        </div>
      </div>
    </div>
  );
}
